import { EMAIL_RE } from '@/utilities/formValidation'

/** Google's pre-hashed enhanced conversion fields; never raw contact details. */
export type EnhancedConversionData = {
  sha256_email_address: string
  sha256_phone_number?: string
}

function normalizeEmail(value: unknown): string | undefined {
  if (typeof value !== 'string') return
  const email = value.trim().toLowerCase()
  if (!EMAIL_RE.test(email)) return
  const [local, domain] = email.split('@')
  return `${domain === 'gmail.com' || domain === 'googlemail.com' ? local.replace(/\./g, '') : local}@${domain}`
}

function normalizePhone(value: unknown): string | undefined {
  if (typeof value !== 'string' || !/^[+\d\s().-]+$/.test(value.trim())) return
  const phone = value.trim().replace(/[\s().-]/g, '')
  if (/^\+[1-9]\d{10,14}$/.test(phone)) return phone
  // Craft's forms collect US numbers. Never guess a country for other formats.
  if (/^\d{10}$/.test(phone)) return `+1${phone}`
  if (/^1\d{10}$/.test(phone)) return `+${phone}`
}

async function sha256(value: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value))
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('')
}

/** Call only after the server accepts the lead. Tracking cannot prevent navigation. */
export async function prepareEnhancedConversionData(contact: {
  email?: unknown
  phone?: unknown
}): Promise<EnhancedConversionData | undefined> {
  try {
    const email = normalizeEmail(contact.email)
    // Google requires email or a complete postal address; phone alone is insufficient.
    if (!email || typeof crypto === 'undefined' || !crypto.subtle) return
    const phone = normalizePhone(contact.phone)
    const data: EnhancedConversionData = { sha256_email_address: await sha256(email) }
    if (phone) data.sha256_phone_number = await sha256(phone)
    return data
  } catch {
    // Unsupported/restricted Web Crypto must not fail an otherwise successful form.
    return undefined
  }
}
